import { useState, useMemo, useCallback, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { SearchInput } from "@/components/SearchInput";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  StatusBadge,
  TypeBadges,
  ProjectStatus,
  ProjectType,
} from "@/components/ProjectBadges";
import { formatShortDateRange } from "@/lib/date-utils";
import {
  Plus,
  MoreHorizontal,
  Trash2,
  RotateCcw,
  FolderOpen,
  ExternalLink,
  Check,
  ChevronsUpDown,
  Filter,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { CreateProjectDialog } from "@/components/calendar/CreateProjectDialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import {
  useProjects,
  useSoftDeleteProject,
  useRestoreProject,
  useProjectWriteAvailability,
  ProjectWithRelations,
} from "@/hooks/useProjects";
import { useProjectGroups } from "@/hooks/useProjectGroups";
import { useCompanies } from "@/hooks/useCompanies";
import { formatProjectName } from "@/lib/format-project-name";
import { matchesSearchTokens } from "@/lib/search-utils";
import {
  startOfDay,
  addWeeks,
  subWeeks,
  subMonths,
  addMonths,
  startOfYear,
  endOfYear,
  addYears,
  differenceInDays,
  parseISO,
} from "date-fns";

type DateFilter =
  | "all"
  | "two-weeks"
  | "past-month"
  | "next-month"
  | "current-year"
  | "next-year";

const dateFilterLabels: Record<DateFilter, string> = {
  all: "All dates",
  "two-weeks": "+/- 2 weeks",
  "past-month": "Past month",
  "next-month": "Next month",
  "current-year": "Current year",
  "next-year": "Next year",
};

const allStatuses: ProjectStatus[] = [
  "initial_contact",
  "offer",
  "confirmed",
  "rejected",
];

const statusLabels: Record<ProjectStatus, string> = {
  initial_contact: "Initial Contact",
  offer: "Offer",
  confirmed: "Confirmed",
  rejected: "Rejected",
};

const allTypes: ProjectType[] = ["live", "film", "gfx_tracking", "consultancy"];

const typeLabels: Record<ProjectType, string> = {
  live: "Live",
  film: "Film",
  gfx_tracking: "GFX & Tracking",
  consultancy: "Consultancy",
};

const defaultStatuses: ProjectStatus[] = [
  "initial_contact",
  "offer",
  "confirmed",
];

interface FilterMultiSelectProps<T extends string> {
  label: string;
  testId?: string;
  options: T[];
  optionLabels: Record<T, string>;
  value: T[];
  onChange: (value: T[]) => void;
  allValues: T[];
}

function FilterMultiSelect<T extends string>({
  label,
  testId,
  options,
  optionLabels,
  value,
  onChange,
  allValues,
}: FilterMultiSelectProps<T>) {
  const [open, setOpen] = useState(false);
  const allSelected = value.length === allValues.length;
  const noneSelected = value.length === 0;

  const toggleOption = (option: T) => {
    if (value.includes(option)) {
      onChange(value.filter((v) => v !== option));
    } else {
      onChange([...value, option]);
    }
  };

  let triggerText: string;
  if (allSelected || noneSelected) {
    triggerText = `All ${label.toLowerCase()}`;
  } else if (value.length <= 2) {
    triggerText = value.map((v) => optionLabels[v]).join(", ");
  } else {
    triggerText = `${value.length} ${label.toLowerCase()}`;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          data-testid={testId}
          className="justify-between h-10 w-full sm:w-auto"
        >
          <span className="truncate">{triggerText}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-48 p-0" align="start">
        <Command>
          <CommandList>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option}
                  value={option}
                  data-testid={testId ? `${testId}-${option}` : undefined}
                  onSelect={() => toggleOption(option)}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value.includes(option) ? "opacity-100" : "opacity-0",
                    )}
                  />
                  {optionLabels[option]}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default function ProjectsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const navigate = useNavigate();

  // Derive filter state from URL params
  const search = searchParams.get("q") || "";
  const dateFilter = (searchParams.get("date") || "two-weeks") as DateFilter;
  const projectGroupFilter = searchParams.get("group") || "all";
  const showDeleted = searchParams.get("deleted") === "1";
  const customerFilter = searchParams.get("customer") || "all";

  const typeFilters = useMemo(() => {
    const raw = searchParams.get("types");
    if (!raw) return allTypes;
    const parsed = raw
      .split(",")
      .filter((t): t is ProjectType => allTypes.includes(t as ProjectType));
    return parsed.length > 0 ? parsed : allTypes;
  }, [searchParams]);

  const statusFilters = useMemo(() => {
    const raw = searchParams.get("status");
    if (!raw) return defaultStatuses;
    return raw
      .split(",")
      .filter((s): s is ProjectStatus =>
        allStatuses.includes(s as ProjectStatus),
      );
  }, [searchParams]);

  const updateParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(updates)) {
            if (value === null) {
              next.delete(key);
            } else {
              next.set(key, value);
            }
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const { data: projects, isLoading } = useProjects(showDeleted);
  const { data: projectGroups } = useProjectGroups();
  const { data: companies } = useCompanies();
  const softDelete = useSoftDeleteProject();
  const restore = useRestoreProject();
  const { canWrite, reason: writeUnavailableReason } =
    useProjectWriteAvailability();

  const handleSearchChange = (value: string) => {
    if (value) {
      updateParams({ q: value, date: "all" });
    } else {
      updateParams({ q: null, date: null });
    }
  };

  const setDateFilter = (value: DateFilter) => {
    updateParams({ date: value === "two-weeks" ? null : value });
  };

  const setProjectGroupFilter = (value: string) => {
    updateParams({ group: value === "all" ? null : value });
  };

  const setCustomerFilter = (value: string) => {
    updateParams({ customer: value === "all" ? null : value });
  };

  const setTypeFilters = (value: ProjectType[]) => {
    const isDefault =
      value.length === allTypes.length &&
      allTypes.every((t) => value.includes(t));
    updateParams({ types: isDefault ? null : value.join(",") });
  };

  const setStatusFilters = (value: ProjectStatus[]) => {
    const isDefault =
      value.length === defaultStatuses.length &&
      defaultStatuses.every((s) => value.includes(s));
    updateParams({ status: isDefault ? null : value.join(",") });
  };

  const setShowDeleted = (value: boolean) => {
    updateParams({ deleted: value ? "1" : null });
  };

  const filteredProjects = useMemo(() => {
    if (!projects) return [];

    const today = startOfDay(new Date());

    // Filter by status
    let result =
      statusFilters.length === allStatuses.length
        ? projects
        : projects.filter(
            (p) =>
              !p.status || statusFilters.includes(p.status as ProjectStatus),
          );

    // Filter by search
    result = result.filter((p) => {
      const searchText = [
        p.name,
        formatProjectName(p.name, p.event_start_date),
        p.companies?.name ?? "",
      ]
        .join(" ")
        .toLowerCase();

      return matchesSearchTokens(searchText, search);
    });

    // Filter by project group
    if (projectGroupFilter !== "all") {
      result = result.filter((p) => p.project_group_id === projectGroupFilter);
    }

    // Filter by customer
    if (customerFilter !== "all") {
      result = result.filter((p) => p.customer_id === customerFilter);
    }

    // Filter by type
    if (typeFilters.length < allTypes.length) {
      result = result.filter((p) =>
        p.types?.some((t) => typeFilters.includes(t as ProjectType)),
      );
    }

    // Apply date filter only if not searching
    if (!search && dateFilter !== "all") {
      result = result.filter((p) => {
        if (!p.event_start_date && !p.event_end_date) return false;

        const projectStart = p.event_start_date
          ? parseISO(p.event_start_date)
          : null;
        const projectEnd = p.event_end_date ? parseISO(p.event_end_date) : null;

        let rangeStart: Date;
        let rangeEnd: Date;

        switch (dateFilter) {
          case "two-weeks":
            rangeStart = subWeeks(today, 2);
            rangeEnd = addWeeks(today, 2);
            break;
          case "past-month":
            rangeStart = subMonths(today, 1);
            rangeEnd = today;
            break;
          case "next-month":
            rangeStart = today;
            rangeEnd = addMonths(today, 1);
            break;
          case "current-year":
            rangeStart = startOfYear(today);
            rangeEnd = endOfYear(today);
            break;
          case "next-year": {
            const nextYearStart = startOfYear(addYears(today, 1));
            rangeStart = nextYearStart;
            rangeEnd = endOfYear(nextYearStart);
            break;
          }
          default:
            return true;
        }

        // Check if project overlaps with the date range
        const projectStartDate = projectStart || projectEnd;
        const projectEndDate = projectEnd || projectStart;

        if (!projectStartDate || !projectEndDate) return false;

        return projectStartDate <= rangeEnd && projectEndDate >= rangeStart;
      });
    }

    // Sort results
    result.sort((a, b) => {
      const aDate = a.event_start_date ? parseISO(a.event_start_date) : null;
      const bDate = b.event_start_date ? parseISO(b.event_start_date) : null;

      if (!aDate && !bDate) return 0;
      if (!aDate) return 1;
      if (!bDate) return -1;

      if (search) {
        // When searching, sort by distance to today
        const aDist = Math.abs(differenceInDays(aDate, today));
        const bDist = Math.abs(differenceInDays(bDate, today));
        return aDist - bDist;
      }

      // Sort based on filter
      switch (dateFilter) {
        case "past-month":
          return bDate.getTime() - aDate.getTime(); // Descending
        case "two-weeks":
        case "next-month":
        case "current-year":
        case "next-year":
          return aDate.getTime() - bDate.getTime(); // Ascending
        default:
          return bDate.getTime() - aDate.getTime(); // Default descending
      }
    });

    return result;
  }, [
    projects,
    search,
    dateFilter,
    statusFilters,
    projectGroupFilter,
    customerFilter,
    typeFilters,
  ]);

  const handleCreate = () => {
    setDialogOpen(true);
  };

  const handleProjectCreated = (projectId: string) => {
    navigate(`/projects/${projectId}/settings`);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader title="Projects">
        <Button
          offlineEdit
          onClick={handleCreate}
          disabled={!canWrite}
          title={writeUnavailableReason ?? undefined}
        >
          <Plus className="h-4 w-4" />
          New Project
        </Button>
      </PageHeader>

      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <div className="flex-1 sm:max-w-80">
            <SearchInput
              value={search}
              onChange={handleSearchChange}
              placeholder="Search projects..."
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            className="sm:hidden"
            onClick={() => setFiltersOpen(!filtersOpen)}
          >
            <Filter className="h-4 w-4 mr-1" />
            Filters
          </Button>
        </div>
        <div
          className={cn(
            "flex-wrap items-center gap-3",
            filtersOpen ? "flex" : "hidden sm:flex",
          )}
        >
          <Select
            value={dateFilter}
            onValueChange={(v) => setDateFilter(v as DateFilter)}
          >
            <SelectTrigger
              className="w-full sm:w-40"
              data-testid="projects-date-filter"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(dateFilterLabels).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={projectGroupFilter}
            onValueChange={setProjectGroupFilter}
          >
            <SelectTrigger
              className="w-full sm:w-48"
              data-testid="projects-group-filter"
            >
              <SelectValue placeholder="Project Group" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All groups</SelectItem>
              {projectGroups
                ?.filter((g) => !g.deleted_at)
                .sort((a, b) => {
                  const aNum = parseInt(a.number || "0", 10);
                  const bNum = parseInt(b.number || "0", 10);
                  return bNum - aNum;
                })
                .map((g) => (
                  <SelectItem key={g.id} value={g.id}>
                    {g.number ? `${g.number} - ${g.name}` : g.name}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          <Select value={customerFilter} onValueChange={setCustomerFilter}>
            <SelectTrigger
              className="w-full sm:w-48"
              data-testid="projects-customer-filter"
            >
              <SelectValue placeholder="Customer" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All customers</SelectItem>
              {companies
                ?.filter((c) => c.types?.includes("customer"))
                .map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          <FilterMultiSelect
            label="Types"
            testId="projects-type-filter"
            options={allTypes}
            optionLabels={typeLabels}
            value={typeFilters}
            onChange={setTypeFilters}
            allValues={allTypes}
          />
          <FilterMultiSelect
            label="Status"
            testId="projects-status-filter"
            options={allStatuses}
            optionLabels={statusLabels}
            value={statusFilters}
            onChange={setStatusFilters}
            allValues={allStatuses}
          />
          <div className="flex items-center space-x-2">
            <Switch
              id="show-deleted"
              checked={showDeleted}
              onCheckedChange={setShowDeleted}
            />
            <Label
              htmlFor="show-deleted"
              className="text-sm text-muted-foreground"
            >
              Show deleted
            </Label>
          </div>
        </div>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <Table data-testid="projects-table">
          <TableHeader>
            <TableRow>
              <TableHead>Number</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Date Range</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Types</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <Skeleton className="h-4 w-16" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-48" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-32" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-40" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-5 w-20" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-5 w-16" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-6 w-6" />
                  </TableCell>
                </TableRow>
              ))
            ) : filteredProjects.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-12">
                  <div className="flex flex-col items-center gap-2 text-muted-foreground">
                    <FolderOpen className="h-8 w-8" />
                    <h3 className="text-lg font-medium text-foreground">
                      No projects found
                    </h3>
                    <p>
                      {search
                        ? "Try adjusting your search"
                        : "Create your first project to get started"}
                    </p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              filteredProjects.map((project) => (
                <ProjectRow
                  key={project.id}
                  project={project}
                  onDelete={(id) => softDelete.mutate(id)}
                  onRestore={(id) => restore.mutate(id)}
                  canWrite={canWrite}
                />
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <CreateProjectDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSuccess={handleProjectCreated}
      />
    </div>
  );
}

interface ProjectRowProps {
  project: ProjectWithRelations;
  onDelete: (id: string) => void;
  onRestore: (id: string) => void;
  canWrite: boolean;
}

function ProjectLinkCell({
  children,
  className,
  linkClassName,
  to,
}: {
  children: ReactNode;
  className?: string;
  linkClassName?: string;
  to: string | null;
}) {
  if (!to) {
    return <TableCell className={className}>{children}</TableCell>;
  }

  return (
    <TableCell className={cn("p-0", className)}>
      <Link
        to={to}
        className={cn(
          "block h-full p-4 text-inherit no-underline",
          linkClassName,
        )}
      >
        {children}
      </Link>
    </TableCell>
  );
}

function ProjectRow({
  project,
  onDelete,
  onRestore,
  canWrite,
}: ProjectRowProps) {
  const projectHref = project.deleted_at ? null : `/projects/${project.id}`;

  return (
    <TableRow
      data-testid={`project-row-${project.id}`}
      className={cn(project.deleted_at ? "opacity-60" : "cursor-pointer")}
    >
      <ProjectLinkCell
        to={projectHref}
        className="font-mono text-sm text-muted-foreground"
      >
        {project.number || "-"}
      </ProjectLinkCell>
      <ProjectLinkCell to={projectHref} className="font-medium">
        <div className="flex items-center gap-2">
          {formatProjectName(project.name, project.event_start_date)}
          {project.deleted_at && (
            <Badge
              variant="outline"
              className="text-destructive border-destructive/30"
            >
              Deleted
            </Badge>
          )}
        </div>
      </ProjectLinkCell>
      <ProjectLinkCell to={projectHref}>
        {project.companies?.name || "-"}
      </ProjectLinkCell>
      <ProjectLinkCell to={projectHref} className="text-sm whitespace-nowrap">
        {formatShortDateRange(
          project.event_start_date,
          project.event_end_date,
        ) || "-"}
      </ProjectLinkCell>
      <ProjectLinkCell to={projectHref}>
        {project.status ? (
          <StatusBadge status={project.status as ProjectStatus} />
        ) : (
          "-"
        )}
      </ProjectLinkCell>
      <ProjectLinkCell to={projectHref}>
        {project.types && project.types.length > 0 ? (
          <TypeBadges types={project.types as ProjectType[]} />
        ) : (
          "-"
        )}
      </ProjectLinkCell>
      <TableCell className="w-12">
        <DropdownMenu>
          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
            <Button
              variant="ghost"
              size="icon-sm"
              data-testid={`project-actions-${project.id}`}
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
            {project.deleted_at ? (
              <DropdownMenuItem
                offlineEdit
                disabled={!canWrite}
                onClick={() => onRestore(project.id)}
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Restore
              </DropdownMenuItem>
            ) : (
              <>
                <DropdownMenuItem asChild>
                  <Link to={`/projects/${project.id}`}>
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Open
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem
                  offlineEdit
                  className="text-destructive"
                  disabled={!canWrite}
                  onClick={() => onDelete(project.id)}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
}
